import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createBrowserRouter, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import App from './App';
import { assertMountTableConsistent, mountOverrides } from './apps/mountOverrides';
import { appRegistry } from './apps/registry.gen';
import { AppSuspense } from './components/AppSuspense';
// ContractDashboard import removed — /contract/:id retired in Phase 0a;
// the component tree is preserved for reference but no longer routed.
import { ProjectList } from './components/ProjectList';
import { CapabilityMatrixPage, ChatHistoryPage, ComplianceHubPage, HomePage, ProposalDashboardV2, ProposalIndexRedirect, ProposalReviewDetailPage, ProposalReviewsPage, SourcesCLINsPage, SourcesFilesPage, SourcesHistoryPage, SourcesLCATsPage, SourcesPage, SourcesPricingPage, SourcesPursuitPage, SourcesTeamPage, VolumeWorkspacePage } from './components/ProposalDashboard/v2';
import { ProposalDocumentsRoute } from './components/ProposalDashboard/v2/documents/ProposalDocumentsRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RequireAdminRoute } from './components/RequireAdminRoute';
import { RequireWorkflowArea } from './components/RequireWorkflowArea';
import { AssistantLayout } from './layouts/AssistantLayout';
import DashboardLayout from './layouts/DashboardLayout';
import { DeliveryLayout } from './layouts/DeliveryLayout';
import { OpportunitiesLayout } from './layouts/OpportunitiesLayout';
import { ProgramLayout } from './layouts/ProgramLayout';
import { ProjectLayout } from './layouts/ProjectLayout';
import { ProposalLayout } from './layouts/ProposalLayout';
import { PursuitRecordLayout } from './layouts/PursuitRecordLayout';
import { RfiRecordLayout } from './layouts/RfiRecordLayout';
import { useWorkflowAreas } from './lib/access/useWorkflowAreas';
import { AssignmentsListPage as AdminAccessPage } from './pages/Admin/Access';
import { AccessRulesPage } from './pages/Admin/AccessRules/AccessRulesPage';
import { ActivityPage } from './pages/Admin/Activity/ActivityPage';
import { ReviewerRequestsPage } from './pages/Admin/Activity/ReviewerRequestsPage';
import { AdminLayout } from './pages/Admin/AdminLayout';
import { GroupsPage } from './pages/Admin/Groups/GroupsPage';
import { CompanyNotificationDefaults } from './pages/Admin/Notifications/CompanyNotificationDefaults';
import { NotificationDeliveryPage } from './pages/Admin/Notifications/NotificationDeliveryPage';
import { PeoplePage } from './pages/Admin/People/PeoplePage';
import { PermissionsPage } from './pages/Admin/Permissions/PermissionsPage';
import { UsagePage } from './pages/Admin/Usage/UsagePage';
import ArtifactStudioPage from './pages/ArtifactStudio/ArtifactStudioPage';
import Assistant from './pages/Assistant';
import AuthPage from './pages/AuthPage';
import { PursuitsListRoute } from './pages/Capture';
import { pursuitSectionRoutes } from './pages/Capture/record/pursuitSectionRoutes';
import Chat from './pages/Chat';
import { CreateProjectPage } from './pages/CreateProject/CreateProjectPage';
import Dashboard from './pages/Dashboard';
import { ActivationGatePage, AuditLegacyRedirect, AuditsPage, AuditTemplatesPage, AwardComparisonPage, BeyondScopePage, CalendarPage, CapabilitiesPage, CLINsPage, CloseoutPage, CommandCenterPage, CompaniesPage, ContactsPage, DeliverablesListPage, DeliverablesPage, DeliveryOverviewPage, DeviationsPage, DocsPage, DutyPage, EvidenceBoardPage, FinancialPage, GlossaryPage, OpsAuditPage, OptionalTasksPage, OrgPage, PeriodsPage, PlacesPage, PricingSheetReviewPage, ProgramHomePage, ProgramSettingsPage, ProgramsListPage, QualityPage, RegistriesPage, RiskPage, RolesPage, TeamsPage, TouchpointsPage, WeeklyRecapPage, WorkroomPage, WorkstreamPage } from './pages/Delivery';
import { PreAwardWorkspaceV2 } from './pages/Delivery/PreAward/v2/PreAwardWorkspaceV2';
import DevDeliverableImportDemo from './pages/DevDeliverableImportDemo';
import DevToolsDemo from './pages/DevToolsDemo';
import Documents from './pages/Documents';
import DocumentUpload from './pages/Documents/DocumentUpload';
import EditorList from './pages/Editor/EditorList';
import EditorPage from './pages/Editor/EditorPage';
import EmailPreferencesPage from './pages/EmailPreferencesPage';
import GrowthHomePage from './pages/GrowthHome/GrowthHomePage';
import { GrowthHubPage } from './pages/GrowthHub/GrowthHubPage';
import { ProposalsListPage, RfiResponsesListPage } from './pages/GrowthWorkspaces';
import Help from './pages/Help';
import History from './pages/History';
import { LandscapeDetailPage, LandscapeFormPage, LandscapeReportPage, LandscapeReportsPage, LandscapeSeatsPage, LandscapesPage } from './pages/Landscapes';
import Library from './pages/Library';
import { RouteErrorPage } from './pages/NotFound/NotFoundPage';
import OciWorkspacePage from './pages/Oci/OciWorkspacePage';
import { OpportunitiesPage } from './pages/Opportunities';
import PolicyConfigPage from './pages/PolicyConfig';
import { ProjectChatList, ProjectChatView, ProjectDocuments, ProjectOverview, ProjectSettings, ProjectTeam } from './pages/Project';
import ProtestWorkspacePage from './pages/Protest/ProtestWorkspacePage';
import { registryRecordRouteObjects } from './pages/Registries/registryRouteObjects';
import { RfiResponseWorkspacePage } from './pages/RfiResponse';
import { RfiSectionGate } from './pages/RfiResponse/workspace/RfiSectionGate';
import SetPasswordPage from './pages/SetPasswordPage';
import Settings from './pages/Settings';
import SharePointDocuments from './pages/SharePointDocuments';
import SignInAccessPage from './pages/SignIn/AccessPage';
import SSOConfigPage from './pages/SSOConfigPage';
import SharedLinkPage from './pages/shared/[token]';
import Vault from './pages/Vault';
// import { VendorLibraryPage } from './pages/VendorLibrary'; // hidden: not ready for GA
const isDev = process.env.NODE_ENV === 'development';
/**
 * TrackingProfileRedirect Component
 * Redirects old /tracking-profiles/* URLs to /opportunities/tracking-profiles/*
 * Preserves query strings and hash fragments for backward compatibility
 */ function TrackingProfileRedirect() {
    const location = useLocation();
    const newPath = location.pathname.replace(/^\/tracking-profiles/, '/opportunities/tracking-profiles');
    const fullPath = newPath + location.search + location.hash;
    return /*#__PURE__*/ _jsx(Navigate, {
        to: fullPath,
        replace: true
    });
}
/**
 * RegistriesRedirect Component
 * Registries moved out of Delivery to a root-level home (/registries) —
 * they're tenant-wide assets consumed by capture and proposal too, not
 * delivery-scoped data. Preserves the ?tab= query so old deep links and
 * ui-cli journeys land on the right tab.
 */ function RegistriesRedirect() {
    const location = useLocation();
    return /*#__PURE__*/ _jsx(Navigate, {
        to: `/registries${location.search}${location.hash}`,
        replace: true
    });
}
/**
 * ContractRedirect Component
 * /contract/:id is retired; a contract's documents are uploaded and
 * processed in the general project workspace, and the synced delivery
 * program is reachable from the global processing tray once the batch
 * completes. Redirecting to the project — not the programs *list* — keeps
 * the just-created contract on screen instead of dead-ending on a blank
 * table.
 */ function ContractRedirect() {
    const { id } = useParams();
    return /*#__PURE__*/ _jsx(Navigate, {
        to: `/project/${id}`,
        replace: true
    });
}
/**
 * ContractV2Redirect Component
 * /delivery/programs/:id/v2 is retired — the program shell is the Contract
 * Project workspace. ?screen=modifications (the only screen that ever had
 * data) maps to Docs → Modifications; everything else lands on program home.
 */ function ContractV2Redirect() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const target = searchParams.get('screen') === 'modifications' ? `/delivery/programs/${id}/docs?tab=modifications` : `/delivery/programs/${id}`;
    return /*#__PURE__*/ _jsx(Navigate, {
        to: target,
        replace: true
    });
}
/**
 * PreAwardV2Redirect Component
 * The V1 pre-award tabs page is retired — it had no award upload flow.
 * The V2 cockpit (Readiness Board + Bid Baseline + Launch with award
 * intake) is the canonical surface.
 */ function PreAwardV2Redirect() {
    const { id } = useParams();
    // Preserve the query string so ?screen=launch deep-links survive a
    // v1-path entry.
    const { search } = useLocation();
    return /*#__PURE__*/ _jsx(Navigate, {
        to: `/delivery/pre-award/${id}/v2${search}`,
        replace: true
    });
}
/**
 * RiskSectionRedirect Component
 * /delivery/programs/:id/risk is the pre-rename alias for what's now
 * "Potential Issues" (SAD-124) — bookmarks and shared links predate the
 * rename. Preserves the query string and hash so deep links survive.
 * Exported so router.test.tsx can exercise it directly against a memory
 * router without pulling in the full app provider tree.
 */ export function RiskSectionRedirect() {
    const { id } = useParams();
    const { search, hash } = useLocation();
    return /*#__PURE__*/ _jsx(Navigate, {
        to: `/delivery/programs/${id}/potential-issues${search}${hash}`,
        replace: true
    });
}
/**
 * DeviationsSectionRedirect Component
 * /delivery/programs/:id/deviations is the pre-rename alias for what's now
 * "Waivers and Tailoring" (SAD-149) — bookmarks and shared links predate the
 * rename. Preserves the query string and hash so deep links survive.
 * Exported so router.test.tsx can exercise it directly against a memory
 * router without pulling in the full app provider tree.
 */ export function DeviationsSectionRedirect() {
    const { id } = useParams();
    const { search, hash } = useLocation();
    return /*#__PURE__*/ _jsx(Navigate, {
        to: `/delivery/programs/${id}/waivers-tailoring${search}${hash}`,
        replace: true
    });
}
// Tally of every app id mounted by appRoutes(...) below — checked against
// the registry by assertMountTableConsistent at module init.
const mountedAppIds = [];
/**
 * Maps the generated app registry (src/apps/registry.gen.ts) onto routes for
 * one parent — undefined for the top level, a segment like 'opportunities'
 * for apps mountOverrides.tsx nests elsewhere. An app absent from that map
 * matches the top level, which is the common case.
 *
 * A splat, not a child list: the app package owns its own route subtree so
 * the whole thing loads as one chunk. AppSuspense sits innermost so a guard
 * that redirects (mountOverrides' `wrap`) never causes the chunk to load.
 */ function appRoutes(parentPath) {
    const prefix = parentPath ? `/${parentPath}/` : '/';
    return appRegistry.filter((app)=>mountOverrides[app.id]?.parentPath === parentPath).map((app)=>{
        mountedAppIds.push(app.id);
        if (!app.basePath.startsWith(prefix)) {
            throw new Error(`router: app '${app.id}' basePath '${app.basePath}' does not sit under its ` + `declared parent '${parentPath ?? '(top level)'}'`);
        }
        const segment = app.basePath.slice(prefix.length);
        const { Component } = app;
        let element = // biome-ignore lint/correctness/useJsxKeyInIterable: not a rendered sibling list — each element is packaged into a RouteObject consumed by createBrowserRouter, which keys by path, not React key.
        /*#__PURE__*/ _jsx(AppSuspense, {
            children: /*#__PURE__*/ _jsx(Component, {})
        });
        const override = mountOverrides[app.id];
        if (override?.wrap) element = override.wrap(element);
        return {
            path: `${segment}/*`,
            element
        };
    });
}
/**
 * Application router configuration
 * Defines all routes and their corresponding page components
 *
 * Route Structure:
 * - Global Mode: /dashboard, /assistant, /documents, /opportunities, /workflows, /history
 * - Project-Scoped Mode: /project/:projectId/* (overview, chat, documents, team, settings)
 * - Proposal Mode: /proposal/:id (full proposal dashboard with batch processing)
 */ /** Resolve only generic entry routes; owning-record deep links keep their destination. */ export function DefaultHome({ dashboard = false }) {
    const { isLoading, growthOnly } = useWorkflowAreas();
    if (isLoading) return /*#__PURE__*/ _jsx("output", {
        children: "Loading your home…"
    });
    if (growthOnly) return /*#__PURE__*/ _jsx(Navigate, {
        to: "/growth/home",
        replace: true
    });
    return dashboard ? /*#__PURE__*/ _jsx(Dashboard, {}) : /*#__PURE__*/ _jsx(Navigate, {
        to: "/dashboard",
        replace: true
    });
}
const router = createBrowserRouter([
    {
        path: '/',
        element: /*#__PURE__*/ _jsx(App, {}),
        // Catches 404s and routing/render errors app-wide — without this,
        // any stale URL shows React Router's raw developer error screen.
        errorElement: /*#__PURE__*/ _jsx(RouteErrorPage, {}),
        children: [
            {
                path: 'login',
                element: /*#__PURE__*/ _jsx(AuthPage, {})
            },
            {
                path: 'sign-in/access',
                element: /*#__PURE__*/ _jsx(SignInAccessPage, {})
            },
            {
                path: 'register',
                element: /*#__PURE__*/ _jsx(AuthPage, {})
            },
            {
                // SAD-953: where an invite email's link lands. Public by design.
                path: 'set-password',
                element: /*#__PURE__*/ _jsx(SetPasswordPage, {})
            },
            {
                // SAD-954: a notification email's "Manage notifications" / "Stop
                // emails like this" links. Public by design.
                path: 'email-preferences',
                element: /*#__PURE__*/ _jsx(EmailPreferencesPage, {})
            },
            {
                path: 'auth/callback',
                element: /*#__PURE__*/ _jsx(DefaultHome, {})
            },
            {
                path: 'shared/:token',
                element: /*#__PURE__*/ _jsx(SharedLinkPage, {})
            },
            {
                path: '/',
                element: /*#__PURE__*/ _jsx(ProtectedRoute, {
                    children: /*#__PURE__*/ _jsx(DashboardLayout, {})
                }),
                children: [
                    // Default redirect to dashboard
                    {
                        index: true,
                        element: /*#__PURE__*/ _jsx(DefaultHome, {})
                    },
                    // Dashboard - main landing page
                    {
                        element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                            area: "opportunities"
                        }),
                        children: [
                            {
                                path: 'growth/home',
                                element: /*#__PURE__*/ _jsx(GrowthHomePage, {})
                            }
                        ]
                    },
                    // Growth hub: Pursuits, Opportunities and Landscapes cards.
                    {
                        element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                            area: [
                                'capture',
                                'opportunities'
                            ]
                        }),
                        children: [
                            {
                                path: 'growth',
                                element: /*#__PURE__*/ _jsx(GrowthHubPage, {})
                            }
                        ]
                    },
                    // GRW-02: Growth workspace lists (behind growth_record_shell_v1; off → /growth).
                    // RFI responses have no workflow area of their own, so they use capture.
                    {
                        element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                            area: "capture"
                        }),
                        children: [
                            {
                                path: 'growth/rfi-responses',
                                element: /*#__PURE__*/ _jsx(RfiResponsesListPage, {})
                            }
                        ]
                    },
                    {
                        element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                            area: "projects"
                        }),
                        children: [
                            {
                                path: 'growth/proposals',
                                element: /*#__PURE__*/ _jsx(ProposalsListPage, {})
                            }
                        ]
                    },
                    {
                        path: 'dashboard',
                        element: /*#__PURE__*/ _jsx(DefaultHome, {
                            dashboard: true
                        })
                    },
                    // Global Chat Routes — AssistantLayout provides the persistent
                    // left explore rail around both the landing and a conversation.
                    {
                        path: 'assistant',
                        element: /*#__PURE__*/ _jsx(AssistantLayout, {}),
                        children: [
                            {
                                index: true,
                                element: /*#__PURE__*/ _jsx(Assistant, {})
                            },
                            {
                                path: ':id',
                                element: /*#__PURE__*/ _jsx(Chat, {})
                            }
                        ]
                    },
                    {
                        path: 'research-assistant',
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: "/assistant",
                            replace: true
                        })
                    },
                    // Artifact Studio (Phase 2) — project-scoped generate/edit/approve
                    {
                        path: 'artifact-studio/:projectId',
                        element: /*#__PURE__*/ _jsx(ArtifactStudioPage, {})
                    },
                    {
                        path: 'oci/:projectId',
                        element: /*#__PURE__*/ _jsx(OciWorkspacePage, {})
                    },
                    {
                        path: 'protest/:projectId',
                        element: /*#__PURE__*/ _jsx(ProtestWorkspacePage, {})
                    },
                    // Settings
                    {
                        path: 'settings',
                        element: /*#__PURE__*/ _jsx(Settings, {})
                    },
                    {
                        path: 'sso-config',
                        element: /*#__PURE__*/ _jsx(SSOConfigPage, {})
                    },
                    {
                        path: 'policy-config',
                        element: /*#__PURE__*/ _jsx(PolicyConfigPage, {})
                    },
                    // Opportunities with nested tracking profiles, and Landscapes
                    // (SAD-533): Growth, so they mount only for a viewer who may list
                    // opportunities (RequireWorkflowArea).
                    {
                        element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                            area: "opportunities"
                        }),
                        children: [
                            {
                                path: 'opportunities',
                                element: /*#__PURE__*/ _jsx(OpportunitiesLayout, {}),
                                children: [
                                    {
                                        index: true,
                                        element: /*#__PURE__*/ _jsx(OpportunitiesPage, {})
                                    },
                                    ...appRoutes('opportunities')
                                ]
                            },
                            {
                                path: 'landscapes',
                                element: /*#__PURE__*/ _jsx(LandscapesPage, {})
                            },
                            {
                                path: 'landscapes/new',
                                element: /*#__PURE__*/ _jsx(LandscapeFormPage, {})
                            },
                            {
                                path: 'landscapes/:id/edit',
                                element: /*#__PURE__*/ _jsx(LandscapeFormPage, {})
                            }
                        ]
                    },
                    // Audience readers can open bounded intelligence without Growth authority.
                    // The detail page independently gates economics and owning data.
                    {
                        path: 'landscapes/:id',
                        element: /*#__PURE__*/ _jsx(LandscapeDetailPage, {})
                    },
                    // GRW-25 seat history: self-gated from the RPC; flag-off redirects to the detail page.
                    {
                        path: 'landscapes/:id/seats',
                        element: /*#__PURE__*/ _jsx(LandscapeSeatsPage, {})
                    },
                    // LAND-07 review briefs: self-gated from the RPC, exactly like the
                    // detail page's own economics panel.
                    {
                        path: 'landscapes/:id/briefs',
                        element: /*#__PURE__*/ _jsx(LandscapeReportsPage, {})
                    },
                    {
                        path: 'landscapes/:id/briefs/:reportId',
                        element: /*#__PURE__*/ _jsx(LandscapeReportPage, {})
                    },
                    // Pursuits (deal threads — Phase 1 capture command room, glass name).
                    // Top-level workspace type, sibling to proposal/contract projects.
                    // The list is guarded like Opportunities; a single pursuit is not —
                    // a viewer may hold Growth on one pursuit without listing them all.
                    {
                        element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                            area: "capture"
                        }),
                        // GRW-14: Pursuits list behind growth_record_shell_v1, else CaptureListPage.
                        children: [
                            {
                                path: 'capture',
                                element: /*#__PURE__*/ _jsx(PursuitsListRoute, {})
                            }
                        ]
                    },
                    {
                        path: 'capture/:id',
                        element: /*#__PURE__*/ _jsx(PursuitRecordLayout, {}),
                        children: [
                            // GRW-15: Home + one route per §3.3 section slug (shell off: today's page at the
                            // index; the layout redirects nested slugs to ?tab=). Unknown slugs go back to Home.
                            ...pursuitSectionRoutes(),
                            {
                                path: '*',
                                element: /*#__PURE__*/ _jsx(Navigate, {
                                    to: "..",
                                    replace: true
                                })
                            }
                        ]
                    },
                    {
                        path: 'rfi-response/:id',
                        element: /*#__PURE__*/ _jsx(RfiRecordLayout, {}),
                        // WRT-05: the §4.2 RFI record pages (RfiSectionGate keeps today's page when
                        // growth_record_shell_v1 is off; response-document pages need writing_workspace_v1).
                        children: [
                            {
                                index: true,
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "home"
                                })
                            },
                            {
                                path: 'document',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "document"
                                })
                            },
                            {
                                path: 'questions',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "questions"
                                })
                            },
                            {
                                path: 'questions/:sectionId',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "question"
                                })
                            },
                            {
                                path: 'capability-statement',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "capability-statement"
                                })
                            },
                            {
                                path: 'reviews',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "reviews"
                                })
                            },
                            {
                                path: 'chat',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "chat"
                                })
                            },
                            {
                                path: 'notice',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "notice"
                                })
                            },
                            {
                                path: 'labor-categories',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "labor-categories"
                                })
                            },
                            {
                                path: 'amendments',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "amendments"
                                })
                            },
                            {
                                path: 'past-answers',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "past-answers"
                                })
                            },
                            {
                                path: 'team',
                                element: /*#__PURE__*/ _jsx(RfiSectionGate, {
                                    page: "team"
                                })
                            },
                            {
                                path: 'legacy',
                                element: /*#__PURE__*/ _jsx(RfiResponseWorkspacePage, {})
                            },
                            {
                                path: '*',
                                element: /*#__PURE__*/ _jsx(Navigate, {
                                    to: ".",
                                    replace: true
                                })
                            }
                        ]
                    },
                    // Legacy tracking profiles redirect
                    {
                        path: 'tracking-profiles/*',
                        element: /*#__PURE__*/ _jsx(TrackingProfileRedirect, {})
                    },
                    // Registries — tenant-wide assets (companies, contacts,
                    // capabilities, vehicles, role types) shared by capture, proposal,
                    // and delivery. Promoted out of /delivery to a root-level home;
                    // the old path redirects here.
                    {
                        path: 'registries',
                        element: /*#__PURE__*/ _jsx(RegistriesPage, {})
                    },
                    // Pricing sheet review (Phase 4 slice 2b). A sibling of /registries
                    // rather than a tab on it: reviewing a staged sheet is a record-scoped
                    // task with its own commit gate, and it is reached from the pricing
                    // sheets list once an upload has been parsed.
                    {
                        path: 'registries/pricing-sheets/:changeSetId',
                        element: /*#__PURE__*/ _jsx(PricingSheetReviewPage, {})
                    },
                    // --- REG-08 registry record routes (registries_records_v1) ---
                    // Full-page records; flag off, each URL redirects to its drawer.
                    ...registryRecordRouteObjects,
                    // --- end REG-08 ---
                    // Delivery domain — 31 entities under /delivery
                    // Per-domain sub-routes (registries, programs, audits,
                    // audit-templates) land in Track B as those pages ship.
                    {
                        path: 'delivery',
                        element: /*#__PURE__*/ _jsx(DeliveryLayout, {}),
                        children: [
                            {
                                index: true,
                                element: /*#__PURE__*/ _jsx(DeliveryOverviewPage, {})
                            },
                            // Phase 5b: COO command center — cross-project delivery rollups
                            // + drill-down into the program pages below.
                            {
                                path: 'command-center',
                                element: /*#__PURE__*/ _jsx(CommandCenterPage, {})
                            },
                            // SAD-1000: the Command Center's drill-down list, same URL filters.
                            {
                                path: 'command-center/deliverables',
                                element: /*#__PURE__*/ _jsx(DeliverablesListPage, {})
                            },
                            {
                                path: 'org',
                                element: /*#__PURE__*/ _jsx(OrgPage, {})
                            },
                            // Legacy home — registries now live at root-level /registries.
                            {
                                path: 'registries',
                                element: /*#__PURE__*/ _jsx(RegistriesRedirect, {})
                            },
                            // Phase 5c-3: cross-portfolio proof/evidence status board.
                            {
                                path: 'evidence-board',
                                element: /*#__PURE__*/ _jsx(EvidenceBoardPage, {})
                            },
                            // Phase 5c: cross-project delivery deadline calendar.
                            {
                                path: 'calendar',
                                element: /*#__PURE__*/ _jsx(CalendarPage, {})
                            },
                            {
                                path: 'programs',
                                children: [
                                    {
                                        index: true,
                                        element: /*#__PURE__*/ _jsx(ProgramsListPage, {})
                                    },
                                    {
                                        path: ':id',
                                        element: /*#__PURE__*/ _jsx(ProgramLayout, {}),
                                        children: [
                                            {
                                                index: true,
                                                element: /*#__PURE__*/ _jsx(ProgramHomePage, {})
                                            },
                                            {
                                                path: 'activation-gate',
                                                element: /*#__PURE__*/ _jsx(ActivationGatePage, {})
                                            },
                                            {
                                                path: 'closeout',
                                                element: /*#__PURE__*/ _jsx(CloseoutPage, {})
                                            },
                                            {
                                                path: 'workroom',
                                                element: /*#__PURE__*/ _jsx(WorkroomPage, {})
                                            },
                                            {
                                                path: 'roles',
                                                element: /*#__PURE__*/ _jsx(RolesPage, {})
                                            },
                                            {
                                                path: 'companies',
                                                element: /*#__PURE__*/ _jsx(CompaniesPage, {})
                                            },
                                            {
                                                path: 'contacts',
                                                element: /*#__PURE__*/ _jsx(ContactsPage, {})
                                            },
                                            {
                                                path: 'clins',
                                                element: /*#__PURE__*/ _jsx(CLINsPage, {})
                                            },
                                            // Teams (D109): the SOW-first index; the route keeps its
                                            // shipped name. /workstreams/contract-wide is the
                                            // "Contract-wide (<period>)" group.
                                            {
                                                path: 'workstreams',
                                                element: /*#__PURE__*/ _jsx(TeamsPage, {})
                                            },
                                            {
                                                path: 'workstreams/:wsId',
                                                element: /*#__PURE__*/ _jsx(WorkstreamPage, {})
                                            },
                                            {
                                                path: 'capabilities',
                                                element: /*#__PURE__*/ _jsx(CapabilitiesPage, {})
                                            },
                                            {
                                                path: 'deliverables',
                                                element: /*#__PURE__*/ _jsx(DeliverablesPage, {})
                                            },
                                            {
                                                path: 'deliverables/:dutyId',
                                                element: /*#__PURE__*/ _jsx(DutyPage, {})
                                            },
                                            // SAD-859: the PM's monthly pass over work beyond the award.
                                            {
                                                path: 'beyond-scope',
                                                element: /*#__PURE__*/ _jsx(BeyondScopePage, {})
                                            },
                                            // Shipped URLs of the retired Tasks and Next 14 Days
                                            // pages land on Deliverables.
                                            {
                                                path: 'tasks',
                                                element: /*#__PURE__*/ _jsx(Navigate, {
                                                    to: "../deliverables",
                                                    replace: true
                                                })
                                            },
                                            {
                                                path: 'weekly-recap',
                                                element: /*#__PURE__*/ _jsx(WeeklyRecapPage, {})
                                            },
                                            {
                                                path: 'deliverable-lookahead',
                                                element: /*#__PURE__*/ _jsx(Navigate, {
                                                    to: "../deliverables",
                                                    replace: true
                                                })
                                            },
                                            {
                                                path: 'settings',
                                                element: /*#__PURE__*/ _jsx(ProgramSettingsPage, {})
                                            },
                                            {
                                                path: 'optional-tasks',
                                                element: /*#__PURE__*/ _jsx(OptionalTasksPage, {})
                                            },
                                            {
                                                path: 'places',
                                                element: /*#__PURE__*/ _jsx(PlacesPage, {})
                                            },
                                            {
                                                path: 'periods',
                                                element: /*#__PURE__*/ _jsx(PeriodsPage, {})
                                            },
                                            {
                                                path: 'glossary',
                                                element: /*#__PURE__*/ _jsx(GlossaryPage, {})
                                            },
                                            // The audit itself is the portaled side panel (opened
                                            // from this page) — it has no route of its own.
                                            {
                                                path: 'audits',
                                                element: /*#__PURE__*/ _jsx(AuditsPage, {})
                                            },
                                            {
                                                path: 'waivers-tailoring',
                                                element: /*#__PURE__*/ _jsx(DeviationsPage, {})
                                            },
                                            // Pre-rename alias — see DeviationsSectionRedirect.
                                            {
                                                path: 'deviations',
                                                element: /*#__PURE__*/ _jsx(DeviationsSectionRedirect, {})
                                            },
                                            {
                                                path: 'quality',
                                                element: /*#__PURE__*/ _jsx(QualityPage, {})
                                            },
                                            {
                                                path: 'potential-issues',
                                                element: /*#__PURE__*/ _jsx(RiskPage, {})
                                            },
                                            // Pre-rename alias — see RiskSectionRedirect.
                                            {
                                                path: 'risk',
                                                element: /*#__PURE__*/ _jsx(RiskSectionRedirect, {})
                                            },
                                            {
                                                path: 'ops-audit',
                                                element: /*#__PURE__*/ _jsx(OpsAuditPage, {})
                                            },
                                            {
                                                path: 'touchpoints',
                                                element: /*#__PURE__*/ _jsx(TouchpointsPage, {})
                                            },
                                            {
                                                path: 'financial',
                                                element: /*#__PURE__*/ _jsx(FinancialPage, {})
                                            },
                                            {
                                                path: 'docs',
                                                element: /*#__PURE__*/ _jsx(DocsPage, {})
                                            }
                                        ]
                                    },
                                    // ContractWorkspaceV2 is retired — the program shell IS the
                                    // Contract Project workspace. The one live screen
                                    // (modifications) moved to Docs → Modifications; the other
                                    // screens were never wired to data. Deep links map across.
                                    {
                                        path: ':id/v2',
                                        element: /*#__PURE__*/ _jsx(ContractV2Redirect, {})
                                    }
                                ]
                            },
                            // Phase 0b: SDS pre-award workspace. Routed under /delivery
                            // (not /proposal) since the pre-award row lives in the
                            // delivery data plane (pangaea.delivery.pre_award_projects)
                            // and the surface composes ryugu/zou delivery hooks.
                            //
                            // V1 (PreAwardDetailPage) is retired: it lacks the award
                            // upload/intake flow, so users landing there had no way to
                            // start award processing. Auto-direct to the V2 cockpit.
                            {
                                path: 'pre-award/:id',
                                element: /*#__PURE__*/ _jsx(PreAwardV2Redirect, {})
                            },
                            // Phase 4d: Pre-Award Workspace V2 cockpit (canonical).
                            {
                                path: 'pre-award/:id/v2',
                                element: /*#__PURE__*/ _jsx(PreAwardWorkspaceV2, {})
                            },
                            // Phase 0b PR 6: Award Comparison workspace. Sibling of the
                            // Pre-Award page; reached via the "Begin Award Comparison"
                            // CTA once a change-set exists.
                            {
                                path: 'pre-award/:id/comparison/:csId',
                                element: /*#__PURE__*/ _jsx(AwardComparisonPage, {})
                            },
                            // Legacy flat audit URL — opens the audit panel on the owning
                            // program's audits page (the audit has no page of its own).
                            {
                                path: 'audits/:id',
                                element: /*#__PURE__*/ _jsx(AuditLegacyRedirect, {})
                            },
                            {
                                path: 'audit-templates',
                                element: /*#__PURE__*/ _jsx(AuditTemplatesPage, {})
                            }
                        ]
                    },
                    // Documents
                    {
                        path: 'documents',
                        element: /*#__PURE__*/ _jsx(Documents, {})
                    },
                    {
                        path: 'documents/upload',
                        element: /*#__PURE__*/ _jsx(DocumentUpload, {})
                    },
                    // Collaborative editor (shirohige + ONLYOFFICE)
                    {
                        path: 'editor',
                        element: /*#__PURE__*/ _jsx(EditorList, {})
                    },
                    {
                        path: 'editor/folder/:folderId',
                        element: /*#__PURE__*/ _jsx(EditorList, {})
                    },
                    {
                        path: 'editor/:documentId',
                        element: /*#__PURE__*/ _jsx(EditorPage, {})
                    },
                    {
                        path: 'sharepoint-documents',
                        element: /*#__PURE__*/ _jsx(SharePointDocuments, {})
                    },
                    // Projects list
                    {
                        path: 'projects',
                        element: /*#__PURE__*/ _jsx(ProjectList, {})
                    },
                    // Create project wizard (must be before project/:projectId)
                    {
                        path: 'projects/create',
                        element: /*#__PURE__*/ _jsx(CreateProjectPage, {})
                    },
                    // General project with nested pages (project-scoped navigation)
                    {
                        path: 'project/:projectId',
                        element: /*#__PURE__*/ _jsx(ProjectLayout, {}),
                        children: [
                            {
                                index: true,
                                element: /*#__PURE__*/ _jsx(ProjectOverview, {})
                            },
                            {
                                path: 'chat',
                                element: /*#__PURE__*/ _jsx(ProjectChatList, {})
                            },
                            {
                                path: 'chat/:chatId',
                                element: /*#__PURE__*/ _jsx(ProjectChatView, {})
                            },
                            {
                                path: 'documents',
                                element: /*#__PURE__*/ _jsx(ProjectDocuments, {})
                            },
                            {
                                path: 'team',
                                element: /*#__PURE__*/ _jsx(ProjectTeam, {})
                            },
                            {
                                path: 'settings',
                                element: /*#__PURE__*/ _jsx(ProjectSettings, {})
                            },
                            // Project-scoped global features (keeps user in project context)
                            {
                                element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                                    area: "opportunities"
                                }),
                                children: [
                                    {
                                        path: 'opportunities',
                                        element: /*#__PURE__*/ _jsx(OpportunitiesPage, {})
                                    }
                                ]
                            },
                            {
                                path: 'sharepoint',
                                element: /*#__PURE__*/ _jsx(SharePointDocuments, {})
                            },
                            {
                                path: 'history',
                                element: /*#__PURE__*/ _jsx(History, {})
                            }
                        ]
                    },
                    // Bare /proposal has no canonical landing — send users to the
                    // proposals list rather than 404'ing on a copy-pasted URL or a
                    // breadcrumb that lost its ID.
                    {
                        path: 'proposal',
                        element: /*#__PURE__*/ _jsx(Navigate, {
                            to: "/projects",
                            replace: true
                        })
                    },
                    // Proposal project with nested pages (index redirect + settings + v2 shell)
                    {
                        path: 'proposal/:id',
                        element: /*#__PURE__*/ _jsx(ProposalLayout, {}),
                        children: [
                            {
                                index: true,
                                element: /*#__PURE__*/ _jsx(ProposalIndexRedirect, {})
                            },
                            {
                                path: 'settings',
                                element: /*#__PURE__*/ _jsx(ProjectSettings, {})
                            },
                            // Proposal workspace (v2 is the only proposal UI since OD-60; the index redirects here).
                            {
                                path: 'v2',
                                element: /*#__PURE__*/ _jsx(ProposalDashboardV2, {}),
                                children: [
                                    {
                                        index: true,
                                        element: /*#__PURE__*/ _jsx(Navigate, {
                                            to: "home",
                                            replace: true
                                        })
                                    },
                                    {
                                        path: 'home',
                                        element: /*#__PURE__*/ _jsx(HomePage, {})
                                    },
                                    {
                                        path: 'compliance',
                                        element: /*#__PURE__*/ _jsx(ComplianceHubPage, {})
                                    },
                                    {
                                        path: 'capability-matrix',
                                        element: /*#__PURE__*/ _jsx(CapabilityMatrixPage, {})
                                    },
                                    {
                                        path: 'reviews',
                                        element: /*#__PURE__*/ _jsx(ProposalReviewsPage, {})
                                    },
                                    {
                                        path: 'reviews/:reviewId',
                                        element: /*#__PURE__*/ _jsx(ProposalReviewDetailPage, {})
                                    },
                                    {
                                        path: 'chat-history',
                                        element: /*#__PURE__*/ _jsx(ChatHistoryPage, {})
                                    },
                                    {
                                        path: 'documents',
                                        element: /*#__PURE__*/ _jsx(ProposalDocumentsRoute, {})
                                    },
                                    // Volume workspace — index redirects so clicking "volume" in
                                    // the breadcrumbs doesn't 404.
                                    {
                                        path: 'volume',
                                        children: [
                                            {
                                                index: true,
                                                element: /*#__PURE__*/ _jsx(Navigate, {
                                                    to: "../home",
                                                    replace: true
                                                })
                                            },
                                            {
                                                path: ':volumeId',
                                                element: /*#__PURE__*/ _jsx(VolumeWorkspacePage, {})
                                            },
                                            {
                                                path: ':volumeId/section/:sectionId',
                                                element: /*#__PURE__*/ _jsx(VolumeWorkspacePage, {})
                                            }
                                        ]
                                    },
                                    // Sources — explicit per-key routes lift the legacy tabs
                                    // unchanged into the V2 SOURCES rail. The :sourceKey route
                                    // catches any future / unknown keys with a placeholder.
                                    {
                                        path: 'sources',
                                        children: [
                                            {
                                                index: true,
                                                element: /*#__PURE__*/ _jsx(Navigate, {
                                                    to: "files",
                                                    replace: true
                                                })
                                            },
                                            {
                                                path: 'files',
                                                element: /*#__PURE__*/ _jsx(SourcesFilesPage, {})
                                            },
                                            {
                                                path: 'clins',
                                                element: /*#__PURE__*/ _jsx(SourcesCLINsPage, {})
                                            },
                                            {
                                                path: 'lcats',
                                                element: /*#__PURE__*/ _jsx(SourcesLCATsPage, {})
                                            },
                                            {
                                                path: 'pricing',
                                                children: [
                                                    {
                                                        index: true,
                                                        element: /*#__PURE__*/ _jsx(SourcesPricingPage, {})
                                                    },
                                                    ...[
                                                        'scenarios',
                                                        ':scenarioId',
                                                        ':scenarioId/:node',
                                                        ':scenarioId/clin/:clinNumber',
                                                        ':scenarioId/tiers/:tierId'
                                                    ].map((path)=>({
                                                            path,
                                                            element: /*#__PURE__*/ _jsx(SourcesPricingPage, {})
                                                        }))
                                                ]
                                            },
                                            {
                                                path: 'pursuit',
                                                element: /*#__PURE__*/ _jsx(SourcesPursuitPage, {})
                                            },
                                            {
                                                path: 'team',
                                                element: /*#__PURE__*/ _jsx(SourcesTeamPage, {})
                                            },
                                            {
                                                path: 'history',
                                                element: /*#__PURE__*/ _jsx(SourcesHistoryPage, {})
                                            },
                                            {
                                                path: ':sourceKey',
                                                element: /*#__PURE__*/ _jsx(SourcesPage, {})
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    // Phase 0.5 Component G — access administration.
                    // Lists role-bundle assignments, grants new ones, supports
                    // break-glass + revoke. Backed by enies-lobby's Admin* RPCs.
                    //
                    // RequireAdminRoute mirrors the backend role check in
                    // services/enies-lobby/pkg/connecthandlers/admin_authz.go::
                    // adminTenant — only `admin` / `internal` principals get
                    // through (Wave 3c retired tenant_security_officer). The
                    // backend is authoritative (the UI guard is for UX); a
                    // non-admin who manipulates the route still hits a
                    // CodePermissionDenied at the RPC.
                    // Unified admin console (polish task 04): /admin with People /
                    // Groups / Access tabs. /admin redirects to People. The backend is
                    // authoritative on every admin RPC; RequireAdminRoute is the UX gate.
                    // Round 7: non-admin designated grantors review from /requests —
                    // same queue as /admin/activity, gated on the server's reviewer
                    // flag instead of the admin role (the backend re-enforces on
                    // every RPC either way).
                    {
                        path: 'requests',
                        element: /*#__PURE__*/ _jsx(ReviewerRequestsPage, {})
                    },
                    {
                        // D12: 'access' is intentionally NOT behind RequireAdminRoute —
                        // AssignmentsListPage is its own gate (server reviewer flag),
                        // rendering the owner-centric self-service "Delegate my access"
                        // panel for everyone else. Every other tab stays admin/internal
                        // only, same as before, just wrapped per-child instead of once
                        // on the parent.
                        path: 'admin',
                        element: /*#__PURE__*/ _jsx(AdminLayout, {}),
                        children: [
                            {
                                index: true,
                                element: /*#__PURE__*/ _jsx(Navigate, {
                                    to: "/admin/people",
                                    replace: true
                                })
                            },
                            {
                                path: 'people',
                                element: /*#__PURE__*/ _jsx(RequireAdminRoute, {
                                    children: /*#__PURE__*/ _jsx(PeoplePage, {})
                                })
                            },
                            {
                                path: 'groups',
                                element: /*#__PURE__*/ _jsx(RequireAdminRoute, {
                                    children: /*#__PURE__*/ _jsx(GroupsPage, {})
                                })
                            },
                            {
                                path: 'permissions',
                                element: /*#__PURE__*/ _jsx(RequireAdminRoute, {
                                    children: /*#__PURE__*/ _jsx(PermissionsPage, {})
                                })
                            },
                            {
                                // FND-25: ABAC access-rule settings (admin-only, like permissions).
                                path: 'access-rules',
                                element: /*#__PURE__*/ _jsx(RequireAdminRoute, {
                                    children: /*#__PURE__*/ _jsx(AccessRulesPage, {})
                                })
                            },
                            {
                                path: 'activity',
                                element: /*#__PURE__*/ _jsx(RequireAdminRoute, {
                                    children: /*#__PURE__*/ _jsx(ActivityPage, {})
                                })
                            },
                            {
                                path: 'notifications',
                                element: /*#__PURE__*/ _jsxs(RequireAdminRoute, {
                                    orCompanySettings: true,
                                    children: [
                                        /*#__PURE__*/ _jsx(CompanyNotificationDefaults, {}),
                                        /*#__PURE__*/ _jsx(NotificationDeliveryPage, {})
                                    ]
                                })
                            },
                            {
                                path: 'access',
                                element: /*#__PURE__*/ _jsx(AdminAccessPage, {})
                            },
                            {
                                path: 'usage',
                                element: /*#__PURE__*/ _jsx(RequireAdminRoute, {
                                    orCompanySettings: true,
                                    children: /*#__PURE__*/ _jsx(UsagePage, {})
                                })
                            }
                        ]
                    },
                    // /contract/:id routes to the general project workspace — see
                    // ContractRedirect for why this is no longer the programs list.
                    {
                        path: 'contract/:id',
                        element: /*#__PURE__*/ _jsx(ContractRedirect, {})
                    },
                    // Other global routes
                    {
                        path: 'help',
                        element: /*#__PURE__*/ _jsx(Help, {})
                    },
                    {
                        path: 'history',
                        element: /*#__PURE__*/ _jsx(History, {})
                    },
                    {
                        element: /*#__PURE__*/ _jsx(RequireWorkflowArea, {
                            area: "library"
                        }),
                        children: [
                            {
                                path: 'library',
                                element: /*#__PURE__*/ _jsx(Library, {})
                            }
                        ]
                    },
                    // hidden: vendor library is not ready for general availability
                    // { path: 'vendor-library', element: <VendorLibraryPage /> },
                    {
                        path: 'vault',
                        element: /*#__PURE__*/ _jsx(Vault, {})
                    },
                    // App packages mounted at the top level (see src/apps/mountOverrides.tsx
                    // for the ones that nest elsewhere instead).
                    ...appRoutes(),
                    // Dev-only routes
                    ...isDev ? [
                        {
                            path: 'dev/tools',
                            element: /*#__PURE__*/ _jsx(DevToolsDemo, {})
                        },
                        {
                            path: 'dev/deliverable-import',
                            element: /*#__PURE__*/ _jsx(DevDeliverableImportDemo, {})
                        }
                    ] : []
                ]
            }
        ]
    }
]);
// Backstop for mountOverrides.tsx drifting from the calls above — see
// assertMountTableConsistent for the two failure shapes it closes.
assertMountTableConsistent(appRegistry, mountOverrides, mountedAppIds);
export default router;
